import { z } from 'zod';
import { PreferredTeeSchema } from '../config/preferred-tee';
import {
  AuthProvider,
  ClubRole,
  ParticipantStatus,
  SessionKind,
  SessionScope,
  TenantKind,
  TenantRole,
  TournamentRole,
} from '../enums';

export const LoginRequestSchema = z.object({
  email: z.string().min(3).max(200),
  password: z.string().min(1).max(200),
  deviceId: z.string().max(64).optional(),
});

/**
 * One-time handoff of a signed-in app user to the web admin console (CLAUDE.md §15 #398): the app
 * asks for a code (POST /auth/handoff, USER session) and opens `<web>/admin#handoff=<code>`; the
 * web redeems it (POST /auth/handoff/redeem) for its own USER session. Single use, 60 s.
 */
export const AuthHandoffResponseSchema = z.object({
  code: z.string(),
  expiresAt: z.iso.datetime(),
});
export type AuthHandoffResponse = z.infer<typeof AuthHandoffResponseSchema>;

export const AuthHandoffRedeemRequestSchema = z.object({
  code: z.string().min(20).max(64),
  deviceId: z.string().max(64).optional(),
});
export type AuthHandoffRedeemRequest = z.infer<typeof AuthHandoffRedeemRequestSchema>;

export const CaddyLoginRequestSchema = z.object({
  /** CADDY_PORTAL link token from /c/:token */
  linkToken: z.string().min(16).max(64),
  code: z.string().min(1).max(20),
  /** required unless the tournament turned the caddy PIN off (policies.caddyPin false) */
  pin: z
    .string()
    .regex(/^\d{4,8}$/)
    .optional(),
  deviceId: z.string().max(64).optional(),
  /**
   * 1 caddy per player (§15 #471): the bib of the player this caddy carries. Matches the
   * organiser's caddy ↔ player link → in; another link → 409 CADDY_BIB_MISMATCH unless
   * `confirmBib`; no link → linked now. The caddy is assigned to that player's group.
   */
  bib: z.number().int().min(1).max(99999).optional(),
  confirmBib: z.boolean().optional(),
});

/** the player a caddy login with a bib is linked to (§15 #471) */
export const CaddyLoginPlayerSchema = z.object({
  participantId: z.uuid(),
  bib: z.number().int(),
  name: z.string(),
  /** groups of that player the caddy now works on (sets "my player" for each on the device) */
  groupIds: z.array(z.uuid()),
});
export type CaddyLoginPlayer = z.infer<typeof CaddyLoginPlayerSchema>;

export const PlayerLinkLoginRequestSchema = z.object({
  /** Participant.accessToken from /p/:token */
  playerToken: z.string().min(16).max(64),
  deviceId: z.string().max(64).optional(),
});

export const SessionResponseSchema = z.object({
  token: z.string(),
  expiresAt: z.iso.datetime(),
  kind: SessionKind,
  /** PLAYER sessions only */
  scope: SessionScope.nullable().optional(),
});
export type SessionResponse = z.infer<typeof SessionResponseSchema>;

/** POST /auth/caddy/login: the session, plus the linked player when a bib was given */
export const CaddySessionResponseSchema = SessionResponseSchema.extend({
  player: CaddyLoginPlayerSchema.optional(),
});
export type CaddySessionResponse = z.infer<typeof CaddySessionResponseSchema>;

export const MeResponseSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('USER'),
    user: z.object({
      id: z.uuid(),
      email: z.string().nullable(),
      displayName: z.string(),
      locale: z.string(),
      isSuperAdmin: z.boolean(),
      /**
       * true after an admin issued a temporary password: every user route except GET /auth/me,
       * POST /auth/password and POST /auth/logout answers 403 PASSWORD_CHANGE_REQUIRED
       */
      mustChangePassword: z.boolean().default(false),
      /** false for a social / phone-only account (no password set); older servers omit it */
      hasPassword: z.boolean().optional(),
      /** preferred tee box (colour key), null = automatic; older servers omit it */
      preferredTee: PreferredTeeSchema.nullable().optional(),
    }),
    tenants: z.array(
      z.object({
        tenantId: z.uuid(),
        name: z.string(),
        role: TenantRole,
        /** PERSONAL = the user's own 나의 대회 space (§15 #405); older servers omit it */
        kind: TenantKind.optional(),
      }),
    ),
    tournaments: z.array(z.object({ tournamentId: z.uuid(), role: TournamentRole })),
    clubs: z.array(
      z.object({ clubId: z.uuid(), tenantId: z.uuid(), name: z.string(), role: ClubRole }),
    ),
  }),
  z.object({
    kind: z.literal('CADDY'),
    caddy: z.object({ id: z.uuid(), code: z.string(), name: z.string() }),
    tournamentId: z.uuid(),
  }),
  z.object({
    kind: z.literal('PLAYER'),
    /** PLAYER_VIEW (personal link) or SELF_ENTRY (club self-entry session) */
    scope: SessionScope,
    participant: z.object({
      id: z.uuid(),
      fullName: z.string(),
      bibNumber: z.number().nullable(),
      /** non-APPROVED participants get a status-only portal (registration status / waitlist) */
      status: ParticipantStatus,
      waitlistPosition: z.number().nullable(),
    }),
    tournamentId: z.uuid(),
  }),
]);
export type MeResponse = z.infer<typeof MeResponseSchema>;

/**
 * POST /auth/me/delete — the signed-in user deletes their own account (USER session only) → 204.
 * 401 INVALID_CREDENTIALS (wrong password; the client keeps its session), 403 for non-USER
 * sessions, 409 SUPER_ADMIN_CANNOT_DELETE, 409 LAST_ADMIN with `AccountDeleteLastAdminDetails`.
 * The account is anonymised and disabled; tournament entries and results held by organisers stay.
 */
export const AccountDeleteRequestSchema = z.object({
  /**
   * required when the account has a password; a social / phone-only account (no password,
   * `MeResponse.user.hasPassword: false`) instead needs a session signed in within the last
   * `REAUTH_WINDOW_MINUTES` (else 401 REAUTH_REQUIRED: sign in again, then delete) — §15 #421
   */
  password: z.string().min(1).max(200).optional(),
  /** typed confirmation */
  confirm: z.literal('DELETE'),
});
export type AccountDeleteRequest = z.infer<typeof AccountDeleteRequestSchema>;

/** `error.details` of 409 LAST_ADMIN from POST /auth/me/delete */
export const AccountDeleteLastAdminDetailsSchema = z.object({
  /** non-SOCIETY tenants where the user is the only ACTIVE TENANT_ADMIN */
  tenantIds: z.array(z.uuid()).optional(),
  /** active clubs where the user is the only CLUB_ADMIN */
  clubIds: z.array(z.uuid()).optional(),
});
export type AccountDeleteLastAdminDetails = z.infer<typeof AccountDeleteLastAdminDetailsSchema>;

// ---- social / phone sign-in (CLAUDE.md §15 #417–#426) -------------------------------------

/* AuthProvider (sign-in methods) lives in ../enums with the other Prisma enum mirrors */

/** a social-only account re-authenticates within this many minutes before deleting itself */
export const REAUTH_WINDOW_MINUTES = 10;

/** Firebase web app config (public values from the Firebase console) */
export const FirebaseWebConfigSchema = z.object({
  apiKey: z.string().min(1),
  authDomain: z.string().min(1),
  projectId: z.string().min(1),
  appId: z.string().optional(),
  messagingSenderId: z.string().optional(),
  storageBucket: z.string().optional(),
});
export type FirebaseWebConfig = z.infer<typeof FirebaseWebConfigSchema>;

/**
 * GET /auth/providers (public) — which sign-in methods this server has keys for, plus the public
 * values a client needs to start them. A provider without its server config is `enabled: false`
 * and clients hide its button.
 */
export const AuthProvidersResponseSchema = z.object({
  email: z.object({ enabled: z.boolean() }),
  kakao: z.object({
    enabled: z.boolean(),
    /** REST API key = OAuth client_id of the authorize URL */
    clientId: z.string().nullable(),
    /** native app key (for a future native SDK; the app uses the browser flow today) */
    nativeAppKey: z.string().nullable(),
    /** the one registered redirect URI: `<WEB_BASE_URL>/auth/callback/kakao` */
    redirectUri: z.string().nullable(),
  }),
  zalo: z.object({
    enabled: z.boolean(),
    appId: z.string().nullable(),
    /** `<WEB_BASE_URL>/auth/callback/zalo`; the flow uses PKCE (S256) */
    redirectUri: z.string().nullable(),
  }),
  firebase: z.object({
    /** Google sign-in through Firebase Authentication */
    google: z.boolean(),
    /** phone number (SMS code) through Firebase Authentication */
    phone: z.boolean(),
    projectId: z.string().nullable(),
    /** null = web sign-in unavailable (the server can still verify ID tokens) */
    web: FirebaseWebConfigSchema.nullable(),
  }),
  /** the web page the app opens in the system browser for a social sign-in (`/auth/app`) */
  appStartUrl: z.string(),
});
export type AuthProvidersResponse = z.infer<typeof AuthProvidersResponseSchema>;

/** PKCE S256 challenge / verifier (base64url, RFC 7636 lengths) */
const pkceChallenge = z.string().regex(/^[A-Za-z0-9_-]{43}$/);
const pkceVerifier = z.string().regex(/^[A-Za-z0-9._~-]{43,128}$/);

/**
 * Extra fields of a sign-in request: `appChallenge` = the request comes from the browser page the
 * app opened (`/auth/app`); instead of a session the server answers a one-time app code bound to
 * this S256 challenge, which only the app holding the verifier can redeem.
 */
const signInExtras = {
  deviceId: z.string().max(64).optional(),
  appChallenge: pkceChallenge.optional(),
  /** first sign-in only: the new account's locale */
  locale: z.enum(['vi', 'en', 'ko', 'zh', 'ja']).optional(),
};

/**
 * POST /auth/social/kakao | /auth/social/zalo — either an authorization code from the redirect to
 * `<WEB_BASE_URL>/auth/callback/<provider>` (exchanged server-side with the client secret; Zalo
 * also needs the PKCE `codeVerifier`), or a provider access token from a native SDK (checked
 * against the provider: Kakao token app id = KAKAO_APP_ID, Zalo appsecret_proof).
 */
const socialCredentialFields = {
  code: z.string().min(1).max(2048).optional(),
  codeVerifier: pkceVerifier.optional(),
  accessToken: z.string().min(1).max(4096).optional(),
};
const oneCredential = (v: { code?: string; accessToken?: string }) =>
  (v.code === undefined) !== (v.accessToken === undefined);
const oneCredentialMessage = { message: 'Send either code or accessToken' };
export const SocialCredentialSchema = z
  .object(socialCredentialFields)
  .refine(oneCredential, oneCredentialMessage);
export type SocialCredential = z.infer<typeof SocialCredentialSchema>;
export const SocialLoginRequestSchema = z
  .object({ ...socialCredentialFields, ...signInExtras })
  .refine(oneCredential, oneCredentialMessage);
export type SocialLoginRequest = z.infer<typeof SocialLoginRequestSchema>;

/** POST /auth/firebase — a Firebase Authentication ID token (sign_in_provider google.com or phone) */
export const FirebaseCredentialSchema = z.object({ idToken: z.string().min(20).max(8192) });
export const FirebaseLoginRequestSchema = FirebaseCredentialSchema.extend(signInExtras);
export type FirebaseLoginRequest = z.infer<typeof FirebaseLoginRequestSchema>;

/** answer of a sign-in request that carried `appChallenge` (single use, 120 s) */
export const AuthAppCodeResponseSchema = z.object({
  appCode: z.string(),
  expiresAt: z.iso.datetime(),
});
export type AuthAppCodeResponse = z.infer<typeof AuthAppCodeResponseSchema>;

/** a session from a social / phone sign-in (also POST /auth/app-code/redeem) */
export const SocialSessionResponseSchema = SessionResponseSchema.extend({
  /** true when this sign-in created the account */
  created: z.boolean().optional(),
  /**
   * set on a new account whose provider e-mail / verified phone already belongs to another
   * account: nothing was merged (§15 #419) — clients suggest signing in to that account and
   * linking this method from the account page instead
   */
  notice: z.enum(['EMAIL_IN_USE', 'PHONE_IN_USE']).optional(),
});
export type SocialSessionResponse = z.infer<typeof SocialSessionResponseSchema>;

/** POST /auth/social/* and /auth/firebase answer a session, or an app code with appChallenge */
export const SocialLoginResponseSchema = z.union([
  SocialSessionResponseSchema,
  AuthAppCodeResponseSchema,
]);
export type SocialLoginResponse = z.infer<typeof SocialLoginResponseSchema>;

/** POST /auth/app-code/redeem (sign in) and POST /auth/me/identities/app-code (link) */
export const AuthAppCodeRedeemRequestSchema = z.object({
  code: z.string().min(20).max(64),
  codeVerifier: pkceVerifier,
  deviceId: z.string().max(64).optional(),
});
export type AuthAppCodeRedeemRequest = z.infer<typeof AuthAppCodeRedeemRequestSchema>;

/** one linked sign-in method of the signed-in user */
export const AuthIdentitySchema = z.object({
  id: z.uuid(),
  provider: AuthProvider,
  /** what the provider reported (display only; may be null) */
  email: z.string().nullable(),
  phoneE164: z.string().nullable(),
  displayName: z.string().nullable(),
  createdAt: z.iso.datetime(),
  lastLoginAt: z.iso.datetime().nullable(),
});
export type AuthIdentity = z.infer<typeof AuthIdentitySchema>;

/**
 * GET /auth/me/identities — and the answer of link / unlink. `hasPassword` = e-mail + password
 * login works (an e-mail and a password are set). The last remaining method cannot be removed
 * (409 LAST_LOGIN_METHOD).
 */
export const AuthIdentitiesResponseSchema = z.object({
  items: z.array(AuthIdentitySchema),
  hasPassword: z.boolean(),
  email: z.string().nullable(),
});
export type AuthIdentitiesResponse = z.infer<typeof AuthIdentitiesResponseSchema>;

/** POST /auth/me/identities/kakao | /zalo */
export const SocialLinkRequestSchema = SocialCredentialSchema;
/** POST /auth/me/identities/firebase */
export const FirebaseLinkRequestSchema = FirebaseCredentialSchema;
